/* © CNES */
import { QueryFunctionContext } from '@tanstack/query-core';
import axios, { AxiosProgressEvent } from 'axios';
import { NavigateFunction } from 'react-router-dom';
import { IDENTIFIER } from '../../components/shared/Constants';
import { CollectionsForUser } from '../../models/CollectionsForUser';
import { ItemFilter } from '../../models/Filter';
import { GeoJsonPolygon } from '../../models/GeoJsonPolygon';
import { ItemsAvailabilities } from '../../models/ItemAvailability';
import { apiConfiguration } from '../APIConfiguration';
import { authenticationAPI } from '../authentication/AuthenticateAPI';
import { handleHttpError } from '../common';
import responseCollectionAcces from '../mock/response_collection_user.json';
import responseMockItem from '../mock/response_item.json';
import responseMockItemAvailability from '../mock/response_item_availability.json';
import responseMock from '../mock/response_items.json';
import responseMockItemsAvailabilities from '../mock/response_items_availabilities.json';
import { buildQueryParams } from './BuildQueryItem';
import { QueryItemsAvailabilitiesModel } from './useGetItemAvailability';

/**
 * Interface for searchItems parameters.
 */
export interface QueryModel {
  params: ItemFilter;
  page?: number;
  bbox: number[] | undefined;
  intersects: GeoJsonPolygon | null;
  selectedMissions?: number[] | null;
}

/**
 * Request to search items on regards with filters.
 * @param queryKey the query parameters.
 * @returns a Promise containing a list of items filtered.
 */
export const searchItems = async ({
  queryKey,
}: QueryFunctionContext<
  [string, QueryModel | undefined, NavigateFunction, Function]
>) => {
  let res: Response | undefined;
  const [, searchParams, navigate, setError400] = queryKey;
  let queryParams: {} = { query: {} };

  if (process.env.NODE_ENV === 'production') {
    const headers = new Headers({
      'Content-Type': 'application/json',
      ...authenticationAPI.getAuthorizationHeaders(),
    });
    if (searchParams) {
      queryParams = buildQueryParams(
        searchParams.params,
        searchParams.bbox,
        searchParams.page,
        searchParams.intersects,
        searchParams.selectedMissions
      );
    }

    res = await fetch(
      `${apiConfiguration.apiBaseUrl}${apiConfiguration.apiCatalog}/items`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(queryParams).replaceAll('\\', ''),
      }
    );

    handleHttpError(res, navigate, setError400);
  } else {
    res = new Response(JSON.stringify(responseMock));
  }

  return res.json();
};

/**
 * Request to get details of a specific item on regards.
 * @param queryKey the query parameters.
 * @returns a Promise containing details of an item.
 */
export const getItemDetails = async ({
  queryKey,
}: QueryFunctionContext<any>): Promise<any> => {
  let res: Response | undefined;
  const [, params, navigate, setError400] = queryKey;
  if (process.env.NODE_ENV === 'production') {
    res = await fetch(
      `${apiConfiguration.apiBaseUrl}${apiConfiguration.apiCatalog}/items`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authenticationAPI.getAuthorizationHeaders(),
        },
        body: JSON.stringify({
          query: {
            [IDENTIFIER]: {
              in: [params.productName],
            },
          },
        }),
      }
    );
    handleHttpError(res, navigate, setError400);
  } else {
    res = new Response(JSON.stringify(responseMockItem));
  }
  return res?.json();
};

/**
 * Request to get the product zip file to download
 * @param productId the product Id
 * @param checksum computed information that deals with data integrity and security
 * @param productName the product name
 * @param setError400 function to set error 400
 * @param setDownloadProgress function to set download progress
 * @param navigate function to navigate to another page
 * @param isAsset boolean to check if the product is an asset
 * @return a Promise containing the product zip file
 */
export const downloadProductZipFile = async (
  productId: string,
  checksum: string,
  productName: string,
  setError400: Function,
  setDownloadProgress: Function,
  navigate: NavigateFunction,
  isAsset: boolean
): Promise<any> => {
  if (process.env.NODE_ENV === 'production') {
    return axios({
      url: `${apiConfiguration.apiBaseUrl}/api/download/${productId}/files/${checksum}`,
      method: 'GET',
      responseType: 'blob', // important
      headers: {
        'Content-Type': 'application/json',
        ...authenticationAPI.getAuthorizationHeaders(),
      },
      onDownloadProgress: (progressEvent: AxiosProgressEvent) => {
        if (progressEvent.total) {
          const percentage = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setDownloadProgress(percentage);
        }
      },
    })
      .then((res) => {
        const temporaryDownloadLink = document.createElement('a');
        temporaryDownloadLink.href = URL.createObjectURL(res.data);

        // Préserver l'extension (composée ou simple) dynamiquement via regex
        // Regex pour extension composée à la fin (ex: .tar.gz)
        const compoundExtRegex = /(\.[^.]+\.[^.]+)$/;
        // Regex pour extension simple à la fin (ex: .zip)
        const simpleExtRegex = /(\.[^.]+)$/;

        let extension = '';
        let baseName = productName;
        const matchCompound = productName.match(compoundExtRegex);
        if (matchCompound) {
          extension = matchCompound[1];
          baseName = productName.slice(0, -extension.length);
        } else {
          const matchSimple = productName.match(simpleExtRegex);
          if (matchSimple) {
            extension = matchSimple[1];
            baseName = productName.slice(0, -extension.length);
          }
        }

        // Sanitize the base name by replacing remaining dots with hyphens, then re-append the extension
        const sanitizedFileName = baseName.replace(/\./g, '-') + extension;

        temporaryDownloadLink.download = sanitizedFileName;
        document.body.appendChild(temporaryDownloadLink);
        temporaryDownloadLink.click();
        temporaryDownloadLink.parentNode?.removeChild(temporaryDownloadLink);
      })
      .catch((error) => {
        console.error('error while downloading file : ', error);
        handleHttpError(error.response, navigate, setError400);
      })
      .finally(() => {
        setDownloadProgress(null);
      });
  } else {
    return axios({
      url: `../../example.zip`, //your url
      method: 'GET',
      responseType: 'blob', // important
      onDownloadProgress: (progressEvent) => {
        setDownloadProgress(progressEvent.progress);
      },
    })
      .then((response) => {
        // create file link in browser's memory
        const href = URL.createObjectURL(response.data);
        // create "a" HTML element with href to file & click
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', 'file.zip'); //or any other extension
        document.body.appendChild(link);
        link.click();

        // // clean up "a" element & remove ObjectURL
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      })
      .catch((error) => {
        console.error('error while downloading file : ', error);
        handleHttpError(error.response, navigate, setError400);
      })
      .finally(() => {
        setDownloadProgress(null);
      });
  }
};

/**
 * Request to get Availability of a specific item on regards.
 * @param queryKey the query parameters.
 * @returns a Promise containing the Availability of an item.
 */
export const getItemAvailability = async ({
  queryKey,
}: QueryFunctionContext<any>): Promise<any> => {
  let res: Response | undefined;
  const [, params, navigate, setError400] = queryKey;
  if (process.env.NODE_ENV === 'production') {
    res = await fetch(
      `${apiConfiguration.apiBaseUrl}/availability/${params.productId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authenticationAPI.getAuthorizationHeaders(),
        },
      }
    );
    handleHttpError(res, navigate, setError400);
  } else {
    res = new Response(JSON.stringify(responseMockItemAvailability));
  }
  return res?.json();
};

/**
 * Request to get Availabilities of listed items on regards.
 * @param queryKey the query parameters.
 * @returns a Promise containing the Availability of an item.
 */
export const getItemsAvailabilities = async ({
  queryKey,
}: QueryFunctionContext<
  [string, QueryItemsAvailabilitiesModel]
>): Promise<ItemsAvailabilities> => {
  let res: Response | undefined;
  const [, params] = queryKey;

  if (process.env.NODE_ENV === 'production') {
    res = await fetch(`${apiConfiguration.apiBaseUrl}/fastavailability`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authenticationAPI.getAuthorizationHeaders(),
      },
      body: JSON.stringify(params),
    });
  } else {
    res = new Response(JSON.stringify(responseMockItemsAvailabilities));
  }
  return res?.json();
};

/**
 * get access to the collection.
 * @param queryKey the query parameters.
 * @returns a list of collection.
 */
export const getAccessCollection = async ({
  queryKey,
}: QueryFunctionContext<
  [string, NavigateFunction, Function]
>): Promise<CollectionsForUser> => {
  let res: Response | undefined;
  const [, navigate, setError400] = queryKey;
  if (process.env.NODE_ENV === 'production') {
    res = await fetch(`${apiConfiguration.apiBaseUrl}/api/stac/collections`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authenticationAPI.getAuthorizationHeaders(),
      },
      body: JSON.stringify({ page: 1, limit: 200 }),
    });
    handleHttpError(res, navigate, setError400);
  } else {
    res = new Response(JSON.stringify(responseCollectionAcces));
  }
  return res.json();
};
